// PetArButton — opens the pet model in iOS AR Quick Look via the
// BoodaArQuickLook native plugin. WKWebView (Capacitor host) does
// NOT intercept `<a rel="ar">` Quick Look links the way mobile
// Safari does, so the v1 anchor-tag approach silently did nothing
// on device — switched to a native bridge that hands the USDZ to
// QLPreviewController. Quick Look auto-detects USDZ and adds the
// "AR" pin-to-the-room button on top.
//
// On non-iOS platforms (web preview, Android) the button is
// hidden — no point teasing AR where the OS can't deliver.
//
// EMOJI-IN-UI EXCEPTION: kids-mode opt-in.

import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { FONT } from "../../lib/tokens";
import { playKidSfx } from "../../lib/kidSfx";
import { supabase } from "../../lib/supabase";
import { BoodaArQuickLook } from "../../lib/boodaArQuickLook";
import { useKidPetAnimations } from "../../lib/useKidPetAnimations";

// Bundle-shipped USDZ entries. Only the always-needed baselines
// (idle + backflip celebration) live in the iOS bundle. Everything
// else streams from Supabase Storage via the pet_animation_assets
// manifest — keeps the app slim and lets the artist drop new
// animations without an App Store update.
const BUNDLED_FILES = {
  idle:     "/img/Alien/alien_idle.usdz",
  backflip: "/img/Alien/backflip.usdz",
};

function isIOSPlatform() {
  if (typeof window === "undefined") return false;
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export default function PetArButton({ userId, pet }) {
  const [busy, setBusy] = useState(false);
  // Manifest of remote animations. The map keys match card keys
  // ('super', 'build', etc.); values are public Supabase Storage
  // URLs. Native side downloads each URL on first chip-tap and
  // caches locally for subsequent taps — Pokemon-Go-style lazy
  // CDN load. Missing manifest entries fall back to bundled
  // backflip → bundled idle.
  const { urlByKey } = useKidPetAnimations();

  // Reflect the latest balances back to the native AR view whenever
  // pet.cards_* change (realtime / optimistic spend / parent grant).
  // The native side dims 0-balance chips so the kid can't trigger
  // an animation they don't own. Ref tracks whether the AR view is
  // currently open so we don't push updates when there's no
  // controller to receive them.
  const arOpenRef = useRef(false);
  const balances = pet ? {
    super: pet.cards_super ?? 0,
    build: pet.cards_build ?? 0,
    boost: pet.cards_boost ?? 0,
    spark: pet.cards_spark ?? 0,
    water: pet.cards_water ?? 0,
    dairy: pet.cards_dairy ?? 0,
  } : {};

  useEffect(() => {
    if (!arOpenRef.current) return;
    BoodaArQuickLook.updateBalances(balances).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pet?.cards_super, pet?.cards_build, pet?.cards_boost,
      pet?.cards_spark, pet?.cards_water, pet?.cards_dairy]);

  // Subscribe once to chip-tap events from the native AR view. The
  // native side fires "cardTapped" { key } whenever the kid taps a
  // positive-balance chip; we call spend_kid_card which decrements
  // the balance, awards XP/stats/HP/mana via the RPC's side effects
  // (migrations 0245-0250), and broadcasts back through realtime so
  // every other surface (deck, stats panel, AR chip badges) catches
  // up. Optimistic UI lives on the native side — we don't need to
  // pre-decrement here.
  useEffect(() => {
    if (!isIOSPlatform() || !userId) return;
    const sub = BoodaArQuickLook.addCardTappedListener(async ({ key }) => {
      if (!key) return;
      // Sparkle fires immediately so the audio lands on the same
      // frame as the native 3D burst + UI ring expansion. The
      // munch ("pet eats the card") layers in only after the RPC
      // confirms — that way a failed spend doesn't get a phantom
      // eating sound.
      try { playKidSfx("sparkle"); } catch { /* noop */ }
      const { error } = await supabase.rpc("spend_kid_card", {
        p_kid_user_id: userId,
        p_card_type:   key,
      });
      if (error) {
        console.warn("[pet-ar] spend failed:", error.message);
        // Re-push the authoritative balances so the optimistic
        // native decrement reverts visually. The closure captures
        // `balances` from the latest render — fine for this no-
        // retry path.
        BoodaArQuickLook.updateBalances(balances).catch(() => {});
        return;
      }
      try { playKidSfx("munch"); } catch { /* noop */ }
    });
    return () => {
      // Capacitor addListener returns a handle (possibly async) with
      // .remove(); guard for both shapes.
      try {
        const handle = sub;
        if (handle && typeof handle.then === "function") {
          handle.then((h) => h?.remove?.()).catch(() => {});
        } else {
          handle?.remove?.();
        }
      } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (!isIOSPlatform()) return null;

  const open = async (mode) => {
    if (busy) return;
    setBusy(true);
    try { playKidSfx("sparkle"); } catch { /* noop */ }
    try {
      // Merge bundled baselines + remote per-card URLs. Native
      // side auto-detects http vs bundle-path by prefix.
      const filenames = { ...urlByKey, ...BUNDLED_FILES };
      // (BUNDLED_FILES wins on key collision — local file beats
      // remote URL for idle/backflip since they're guaranteed
      // available offline and don't need a download spinner.)
      arOpenRef.current = true;
      // mode picks "preview" (rotatable 3D stage, no camera) vs
      // "ar" (ARKit camera feed + world placement). Same plugin
      // entry; different presentation.
      await BoodaArQuickLook.presentInteractive(filenames, balances, { mode });
    } catch (e) {
      console.warn("[pet-ar] present failed:", e?.message || e);
    } finally {
      // Reset shortly after the Quick Look modal is presented so a
      // re-tap after dismissing the AR view works without staying
      // stuck in the busy state. The arOpenRef stays true until the
      // user explicitly dismisses; updates to balances while AR is
      // open will get pushed via updateBalances.
      setTimeout(() => setBusy(false), 600);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        marginTop: 14,
        justifyContent: "center",
        flexWrap: "wrap",
      }}
    >
      {/* VIEW IN 3D — rotatable stationary scene, no camera permission. */}
      <button
        type="button"
        onClick={() => open("preview")}
        disabled={busy}
        className="mcm-focusable"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 18px",
          background: "linear-gradient(135deg, #9B5DE5 0%, #7A4ECF 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 999,
          fontFamily: FONT.mono,
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.12em",
          cursor: busy ? "wait" : "pointer",
          opacity: busy ? 0.7 : 1,
          touchAction: "manipulation",
          boxShadow: "0 6px 18px rgba(155, 93, 229, 0.4)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>🌌</span>
        <span>VIEW IN 3D</span>
      </button>
      {/* SEE IN AR — ARKit camera + place-on-floor real-world. */}
      <button
        type="button"
        onClick={() => open("ar")}
        disabled={busy}
        className="mcm-focusable"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 18px",
          background: "linear-gradient(135deg, #5DA9E9 0%, #3D6BB8 100%)",
          color: "#fff",
          border: "none",
          borderRadius: 999,
          fontFamily: FONT.mono,
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.12em",
          cursor: busy ? "wait" : "pointer",
          opacity: busy ? 0.7 : 1,
          touchAction: "manipulation",
          boxShadow: "0 6px 18px rgba(93, 169, 233, 0.4)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>🪐</span>
        <span>SEE IN AR</span>
      </button>
    </div>
  );
}
